import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthResolveSkeleton } from "@/components/layout/AuthResolveSkeleton";
import { SEO as Seo } from "@/components/seo/SEO";
import { getMyCompanies, getMyCompaniesWithRetries } from "@/services/subscriptionService";
import { useQueryClient } from "@tanstack/react-query";
import {
  buildReturnPath,
  consumeJustSignedOut,
  setPostLoginRedirect,
} from "@/utils/postLoginRedirect";
import { getAuthLoginPathFromLocation } from "@/utils/authNavigation";
import { isProtectedPath, isAuthRoutePath, isPublicMarketingRoute } from "@/utils/protectedRoutes";
import {
  getAuthResolveDelayMs,
  shouldDeferUnauthenticatedRedirect,
} from "@/utils/authSessionGrace";
import { DashboardPeriodProvider } from "@/context/DashboardPeriodContext";
import { PolicyAckGate } from "@/components/policy/PolicyAckGate";
import { isFamilyHubDevPreviewActive } from "@/utils/familyHubDevPreview";
import {
  clearRedirectFlagsOnProtectedRoute,
  devProtectedLayoutLog,
  resolvePostLoginDestination,
  resolveProtectedLayoutRenderPhase,
  shouldRedirectJustLoggedInOnPublicRoute,
  shouldResetRedirectOnPublicRoute,
} from "@/components/layout/protectedLayoutHelpers";

/**
 * Protected Layout.
 */
export default function ProtectedLayout() {
  const { user, session, loading, mfaChallenge } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const hasRedirectedRef = useRef(false);
  const authResolvedRef = useRef(false);
  const [authResolved, setAuthResolved] = useState(false);
  const companyCheckDoneRef = useRef(false);
  const hrRedirectCheckDoneRef = useRef(false);
  /** Latched before effects clear sessionStorage — survives async auth/session resolution. */
  const justLoggedInForHrRef = useRef(false);
  const [dashboardHrCheckComplete, setDashboardHrCheckComplete] = useState(false);

  if (user && sessionStorage.getItem("just_logged_in") === "true") {
    justLoggedInForHrRef.current = true;
  }

  const redirectUnauthenticated = useCallback(
    (reason: "protected" | "signed-out") => {
      if (hasRedirectedRef.current) return;
      hasRedirectedRef.current = true;

      if (reason === "signed-out") {
        globalThis.location.replace("/");
        return;
      }

      const returnPath = buildReturnPath(location.pathname, location.search, location.hash);
      setPostLoginRedirect(returnPath);
      navigate(getAuthLoginPathFromLocation(location), { replace: true });
    },
    [location, navigate]
  );

  // Track when auth has truly resolved — grace for session restore / OAuth (see authSessionGrace)
  useEffect(() => {
    if (loading) {
      authResolvedRef.current = false;
      setAuthResolved(false);
      return undefined;
    }

    if (user) {
      authResolvedRef.current = true;
      setAuthResolved(true);
      return undefined;
    }

    authResolvedRef.current = false;
    setAuthResolved(false);
    const delayMs = getAuthResolveDelayMs(false);

    if (delayMs === 0) {
      authResolvedRef.current = true;
      setAuthResolved(true);
      return undefined;
    }

    const timer = setTimeout(() => {
      authResolvedRef.current = true;
      setAuthResolved(true);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [user, loading]);

  // Consolidated redirect for logged-out visitors on protected routes
  useEffect(() => {
    if (loading || user || mfaChallenge || hasRedirectedRef.current) return;
    if (shouldDeferUnauthenticatedRedirect(!!user, authResolved)) return;
    if (!authResolved) return;

    const currentPath = location.pathname;
    if (
      import.meta.env.DEV &&
      currentPath === "/family" &&
      isFamilyHubDevPreviewActive(location.search)
    ) {
      return;
    }
    if (!isProtectedPath(currentPath)) return;

    const signedOut = consumeJustSignedOut();
    redirectUnauthenticated(signedOut ? "signed-out" : "protected");
  }, [
    authResolved,
    loading,
    user,
    mfaChallenge,
    redirectUnauthenticated,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (!user) {
      justLoggedInForHrRef.current = false;
    }
  }, [user]);

  // Safety net: when on /dashboard, if user is HR, redirect to company portal (catches timing races at login)
  // Block rendering until check completes to prevent flash of user dashboard
  useEffect(() => {
    if (location.pathname !== "/dashboard") {
      setDashboardHrCheckComplete(true); // Not on dashboard, no check needed
      return;
    }
    if (
      !user ||
      !authResolvedRef.current ||
      hrRedirectCheckDoneRef.current ||
      !session?.access_token
    ) {
      if (user && authResolvedRef.current) setDashboardHrCheckComplete(true); // Skip check if no token
      return;
    }
    setDashboardHrCheckComplete(false); // Block render until check completes
    hrRedirectCheckDoneRef.current = true;

    const fetchCompanies = () =>
      queryClient.fetchQuery({
        queryKey: ["myCompanies", user.id, session.access_token ? "token" : "no-token"],
        queryFn: () => getMyCompanies(session.access_token),
        staleTime: 60 * 1000,
      });

    // Retries only after fresh login (JWT propagation). Read latched ref — sessionStorage may
    // already be cleared by other effects before auth/session resolve on this mount.
    const justLoggedIn = justLoggedInForHrRef.current;
    if (justLoggedIn) {
      justLoggedInForHrRef.current = false;
      sessionStorage.removeItem("just_logged_in");
    }
    const loadCompanies = justLoggedIn
      ? getMyCompaniesWithRetries(session.access_token, { maxRetries: 2, delays: [200, 400] })
      : fetchCompanies();

    loadCompanies
      .then((companies) => {
        if (companies.length > 0) {
          navigate(`/c/${companies[0].slug}`, { replace: true });
        } else {
          setDashboardHrCheckComplete(true);
        }
      })
      .catch(() => {
        hrRedirectCheckDoneRef.current = false;
        setDashboardHrCheckComplete(true);
      });
  }, [user, session, navigate, location.pathname, queryClient]);

  // Company signup flow: if user signed up as Company and has no companies yet, redirect to create-company when they land on dashboard
  useEffect(() => {
    if (
      !user ||
      !authResolvedRef.current ||
      location.pathname !== "/dashboard" ||
      companyCheckDoneRef.current
    ) {
      return;
    }
    const signupType = user.user_metadata?.signup_type;
    if (signupType !== "company") return;

    companyCheckDoneRef.current = true;
    queryClient
      .fetchQuery({
        queryKey: ["myCompanies", user.id, session?.access_token ? "token" : "no-token"],
        queryFn: () => getMyCompanies(session?.access_token),
        staleTime: 60 * 1000,
      })
      .then((companies) => {
        if (companies.length === 0) {
          navigate("/create-company", { replace: true });
        }
      })
      .catch(() => {
        companyCheckDoneRef.current = false;
      });
  }, [user, session, navigate, location.pathname, queryClient]);

  // CRITICAL: Only redirect to /auth if:
  // 1. Auth has truly resolved (authResolvedRef)
  // 2. No user exists (!user)
  // 3. We haven't already redirected (prevent loops)
  // 4. We're not already on /auth (prevent redirect loops)
  useEffect(() => {
    const currentPath = location.pathname;
    const isOnProtectedRoute = isProtectedPath(currentPath);
    const isOnAuthRoute = isAuthRoutePath(currentPath);
    const isOnPublicRoute = isPublicMarketingRoute(currentPath);

    devProtectedLayoutLog("🔍 ProtectedLayout: Auth state", {
      user: !!user,
      loading,
      authResolved: authResolvedRef.current,
      mfaChallenge,
      currentPath,
      isOnAuthRoute,
      isOnPublicRoute,
      isOnProtectedRoute,
      hasRedirected: hasRedirectedRef.current,
    });

    clearRedirectFlagsOnProtectedRoute(currentPath);

    if (shouldResetRedirectOnPublicRoute(isOnPublicRoute, !!user, authResolvedRef.current)) {
      hasRedirectedRef.current = false;
    }

    const justLoggedIn = sessionStorage.getItem("just_logged_in") === "true";
    if (
      shouldRedirectJustLoggedInOnPublicRoute(
        {
          currentPath,
          user: !!user,
          authResolved: authResolvedRef.current,
          hasRedirected: hasRedirectedRef.current,
          mfaChallenge: !!mfaChallenge,
          justLoggedIn,
        },
        isOnPublicRoute
      )
    ) {
      devProtectedLayoutLog("✅ ProtectedLayout: User just logged in on public route, redirecting");
      hasRedirectedRef.current = true;
      sessionStorage.removeItem("just_logged_in");
      resolvePostLoginDestination(session?.access_token).then((path) => {
        globalThis.location.replace(path);
      });
      return;
    }

    // If MFA challenge is active, redirect to auth page to show MFA prompt
    // CRITICAL: Must be in useEffect, never during render
    if (mfaChallenge && !hasRedirectedRef.current && !isOnAuthRoute && authResolvedRef.current) {
      devProtectedLayoutLog(
        "🔒 ProtectedLayout: MFA challenge active, redirecting to auth for TOTP prompt"
      );
      hasRedirectedRef.current = true;
      navigate(getAuthLoginPathFromLocation(location), { replace: true });
    }
  }, [user, session, loading, mfaChallenge, navigate, location, authResolved]);

  // Reset redirect flag when user is authenticated and on a protected route
  // Also clear the redirecting_to_dashboard flag once we're on a protected route
  // CRITICAL: Only clear flags when auth has resolved to prevent race conditions
  // MUST be before any early returns to follow React hooks rules
  useEffect(() => {
    if (user && !mfaChallenge && !loading && authResolvedRef.current) {
      hasRedirectedRef.current = false;
      if (isProtectedPath(location.pathname)) {
        sessionStorage.removeItem("redirecting_to_dashboard");
        sessionStorage.removeItem("just_logged_in");
        devProtectedLayoutLog("✅ ProtectedLayout: Cleared redirect flags on protected route", {
          path: location.pathname,
        });
      }
    }
  }, [user, loading, mfaChallenge, location.pathname]);

  const renderPhase = resolveProtectedLayoutRenderPhase({
    loading,
    user: !!user,
    mfaChallenge: !!mfaChallenge,
    authResolved,
    currentPath: location.pathname,
    search: location.search,
    dashboardHrCheckComplete,
    isFamilyHubDevPreviewActive,
    shouldDeferUnauthenticatedRedirect,
  });

  if (renderPhase === "loading") {
    devProtectedLayoutLog(
      "⏳ ProtectedLayout: Showing loading state - waiting for auth to resolve",
      {
        loading,
        authResolved: authResolvedRef.current,
        user: false,
      }
    );
    return <AuthResolveSkeleton />;
  }

  if (renderPhase === "public-null") {
    return null;
  }

  if (renderPhase === "mfa-wait") {
    devProtectedLayoutLog(
      "⏳ ProtectedLayout: MFA challenge active, waiting for redirect (in useEffect)"
    );
    return null;
  }

  if (renderPhase === "dashboard-hr-wait") {
    return <AuthResolveSkeleton message="Loading dashboard..." />;
  }

  if (renderPhase === "auth-grace") {
    return <AuthResolveSkeleton />;
  }

  if (renderPhase === "family-dev-preview") {
    return (
      <>
        <Seo
          title="TellerWand Family Hub (preview)"
          description="Dev preview of Family Hub with sample data"
          noIndex={true}
        />
        <DashboardPeriodProvider>
          <Outlet />
        </DashboardPeriodProvider>
      </>
    );
  }

  if (renderPhase === "redirect-sign-in") {
    return <AuthResolveSkeleton message="Redirecting to sign in..." />;
  }

  devProtectedLayoutLog(
    "✅ ProtectedLayout: User authenticated and MFA verified, rendering content"
  );

  return (
    <>
      <Seo
        title="TellerWand Dashboard"
        description="TellerWand personal finance management dashboard"
        noIndex={true}
      />
      <DashboardPeriodProvider>
        <PolicyAckGate>
          <Outlet />
        </PolicyAckGate>
      </DashboardPeriodProvider>
    </>
  );
}
